import type * as React from "react"
import type { DraftKind, DraftStatus } from "@steelbill/types"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * `DRAFT/INV/12`, `READY/QUOT/7` — the name of a saved form, and its state.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE NAME CARRIES THE STATE, which is why there is no second badge beside it.
 *
 * A draft has no document number (§6 — a number is allocated inside the save
 * transaction, and a form that consumed one would leave a gap the CA reads as a
 * missing document). So this IS its name: the owner can say "throw away
 * READY/INV/12" out loud and be understood.
 *
 * Reading left to right it answers the three questions in order — is it
 * finished, what kind of thing is it, which one:
 *
 *   READY / QUOT / 7
 *   ─────   ────   ─
 *   state   kind   the draft row's own id
 *
 * THE ID IS THE DRAFT ROW'S, never anything from either document series. The
 * `DRAFT/` or `READY/` lead is what says "no legal number yet".
 *
 * THE KIND IS IN THE NAME because both kinds sit in one list on the home screen
 * sharing one id sequence. `READY/7` alone would send the owner into the bill
 * editor expecting the quotation he tapped.
 *
 * COLOUR: amber while DRAFT, grey once READY. Amber is the `waiting` tone
 * documents wear elsewhere — a draft is unfinished work asking for attention.
 * READY is settled: checked, nothing more to do until somebody spends a number
 * on it, so it stops competing for the eye. The WORD changes too, never the
 * colour alone (§11.1).
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * NOT TRANSLATED, and not in the locale files.
 *
 * These are document-code tokens, like the `INV/26-27/67` printed on a bill or
 * the `HSN` column head — they read identically in English and Marathi, and a
 * translated one would break the thing the badge exists for: being sayable and
 * searchable as a single fixed string. They were in `en.json`/`mr.json` until
 * 2026-08-15, where they had already drifted (`QTN` in the badge against `QOT`
 * in `formatQuoteNumber`).
 *
 * `CHAL` is defined though nothing writes it yet — challan drafts are §17.7, and
 * this map is keyed by `DraftKind` so the compiler will require an entry the day
 * that kind becomes reachable rather than falling through to a wrong label.
 */
const KIND_TOKEN: Record<DraftKind, string> = {
  INVOICE: "INV",
  QUOTATION: "QUOT",
  CHALLAN: "CHAL",
}

const STATUS_TOKEN: Record<DraftStatus, string> = {
  DRAFT: "DRAFT",
  READY: "READY",
}

/** Amber while it is being worked on; grey once it is finished. */
const TONE: Record<DraftStatus, string> = {
  DRAFT:
    "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300",
  READY:
    "border-zinc-300 bg-zinc-100 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
}

/** `DRAFT/INV/12` — the string on its own, for a title, a toast or a filename. */
export function draftName(kind: DraftKind, id: number, status: DraftStatus = "DRAFT"): string {
  return `${STATUS_TOKEN[status]}/${KIND_TOKEN[kind]}/${String(id)}`
}

export function DraftNameBadge({
  kind,
  id,
  status = "DRAFT",
  className,
}: {
  kind: DraftKind
  id: number
  /** Defaults to DRAFT so a caller that has not loaded the row yet is honest. */
  status?: DraftStatus
  className?: string
}): React.JSX.Element {
  return (
    <Badge variant="outline" className={cn(TONE[status], "tabular-nums", className)}>
      {draftName(kind, id, status)}
    </Badge>
  )
}
